import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { PaperPlaneTilt, ChatCircleDots } from "@phosphor-icons/react";

export default function Messages() {
  const { t } = useLang();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(null); // {job_id, other_id, name}
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const loadConvos = () => api.get("/conversations").then((r) => setConvos(r.data));
  useEffect(() => { loadConvos(); }, []);

  useEffect(() => {
    const jobId = params.get("job"); const userId = params.get("user");
    if (jobId && userId) setActive({ job_id: jobId, other_id: userId, name: null });
  }, [params]);

  useEffect(() => {
    if (!active) return;
    const load = () => api.get(`/messages/${active.job_id}/${active.other_id}`).then((r) => setMsgs(r.data));
    load();
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [active]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    await api.post("/messages", { job_id: active.job_id, to_user_id: active.other_id, text });
    setText("");
    const r = await api.get(`/messages/${active.job_id}/${active.other_id}`); setMsgs(r.data);
    loadConvos();
  };

  return (
    <div className="max-w-5xl mx-auto px-5 py-8">
      <h1 className="font-head text-2xl font-bold tracking-tight mb-4">{t("nav_messages")}</h1>
      <div className="grid md:grid-cols-3 gap-4 bg-white border border-zinc-200 rounded-md overflow-hidden" style={{ minHeight: "60vh" }}>
        <div className="border-r border-zinc-200 divide-y divide-zinc-100 overflow-y-auto max-h-[70vh]">
          {convos.length === 0 && <p className="p-4 text-sm text-zinc-400">—</p>}
          {convos.map((c) => (
            <button key={`${c.job_id}:${c.other_user?.id}`} data-testid={`convo-${c.other_user?.id}`}
              onClick={() => setActive({ job_id: c.job_id, other_id: c.other_user?.id, name: c.other_user?.name })}
              className={`w-full text-left px-4 py-3 hover:bg-zinc-50 transition-colors ${active?.other_id === c.other_user?.id && active?.job_id === c.job_id ? "bg-[#E11D2A]/5" : ""}`}>
              <div className="font-semibold text-sm">{c.other_user?.name}</div>
              <div className="text-xs text-zinc-500 truncate">{c.job_title}</div>
              <div className="text-xs text-zinc-400 truncate mt-0.5">{c.last}</div>
            </button>
          ))}
        </div>
        <div className="md:col-span-2 flex flex-col">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-zinc-400 gap-2"><ChatCircleDots size={40} /><span className="text-sm">{t("nav_messages")}</span></div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-2 max-h-[60vh]">
                {msgs.map((m) => (
                  <div key={m.id} className={`flex ${m.from_user_id === user.id ? "justify-end" : "justify-start"}`}>
                    <div data-testid="message-bubble" className={`max-w-[75%] px-3 py-2 rounded-lg text-sm ${m.from_user_id === user.id ? "bg-[#E11D2A] text-white" : "bg-zinc-100 text-zinc-800"}`}>{m.text}</div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <form onSubmit={send} className="border-t border-zinc-200 p-3 flex gap-2">
                <input data-testid="message-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={t("write_message")} className="flex-1 border border-zinc-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E11D2A]" />
                <button data-testid="send-message" className="bg-[#E11D2A] text-white rounded-md px-4 hover:bg-[#B0121F] transition-colors"><PaperPlaneTilt size={18} weight="fill" /></button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
